<template>
  <svg id="barChartVert"></svg>
</template>

<script>
import * as d3 from 'd3';

export default {
  props: {
    dataInput: Array,
    width: Number,
    height: Number,
    mean: Boolean,
    median: Boolean,
    standDeviation: Boolean,
    variance: Boolean,
    suggest: Boolean,
  },
  mounted() {
    this.drawChart();
  },
  methods: {
    drawChart() {
      const data = this.dataInput;

      let temp = [...data];
      temp.sort((a,b) => a.value - b.value);

      let maxValue = 100;
      /* if (temp.length != 0) {
        maxValue = temp[temp.length-1].value;
      }
      if (maxValue == 0) {
        maxValue = 100;
      } */

      const suggestions = { lower: 30, middle: 50, upper: 80 };

      const remove = d3.select('#barChartVert');
      remove.selectAll('*').remove();

      const margin = { top: 40, right: 160, bottom: 40, left: 60 };
      const width = this.width;
      const height = this.height;

      const svg = d3
        .select('#barChartVert')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .style("font-family", "Times New Roman")
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // Add bounding box with transparent fill and light stroke
      svg
        .append('rect')
        .attr('x', -margin.left)
        .attr('y', -margin.top)
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .attr('rx', 12)
        .attr('fill', 'none')
        .attr('stroke', '#ccc')
        .attr('stroke-width', 1);

      const xScale = d3
        .scaleBand()
        .range([0, width])
        .domain(data.map(d => d.variable))
        .padding(0.6);

      const yScale = d3
        .scaleLinear()
        .domain([0, maxValue])
        .range([height, 0]);

      // Add Y axis
      svg
        .append('g')
        .call(d3.axisLeft(yScale).tickSize(5));

      // Bars color scale
      const colorScale = d3.scaleOrdinal()
        .domain(data.map(d => d.variable))
        .range(data.map(d => {
          if (d.value < suggestions.lower) {
            return '#F87B7B';
          } else if (d.value < suggestions.middle) {
            return '#FD9B41';
          } else {
            return '#14AE5C';
          }
        }));

      // Create bars
      svg
        .selectAll('mybar')
        .data(data)
        .enter()
        .append('rect')
        .attr('x', d => xScale(d.variable))
        .attr('y', d => yScale(d.value))
        .attr('width', xScale.bandwidth())
        .attr('height', d => height - yScale(d.value))
        .attr('fill', d => colorScale(d.variable))
        .attr('rx', 5)
        .attr('ry', 5);

      // X axis with rotated labels
      svg
        .append('g')
        .attr('transform', `translate(0, ${height})`)
        .call(d3.axisBottom(xScale).tickSize(0))
        .selectAll('text')
        .attr('transform', 'rotate(-45)')
        .style('text-anchor', 'end')
        .attr('dx', '-0.6em')
        .attr('dy', '0.15em');

      svg.append('text')
        .attr('text-anchor', 'middle')
        .attr('transform', `translate(${-margin.left + 25}, ${height / 2}) rotate(-90)`)
        .attr('font-size', '13px')
        .attr('fill', '#333')
        .text('Score (%)');


      svg.append('text')
        .attr('text-anchor', 'middle')
        .attr('x', width / 2)
        .attr('y', height + margin.bottom - 15)
        .attr('font-size', '13px')
        .attr('fill', '#333')
        .text('Categories');

      // Suggestions axis on right side
      if (this.suggest) {
        const suggestionsAxis = d3
          .axisRight(yScale)
          .tickValues([suggestions.lower, suggestions.middle, suggestions.upper])
          .tickSizeInner(-(width + margin.left + margin.right) + margin.left * 4)
          .tickFormat((d, i) => ['need improve', 'good', 'excellent'][i]);

        svg
          .append('g')
          .attr('class', 'suggestionsAxis')
          .attr('transform', `translate(${width + 10}, 0)`)
          .call(suggestionsAxis)
          .select('.domain')
          .remove();

        svg
          .selectAll('.suggestionsAxis .tick line')
          .attr('stroke-dasharray', '2,2')
          .style('stroke', (d, i) => ['#F87B7B', '#FD9B41', '#14AE5C'][i]);
      }

      // Calculate stats
      const meanValue = d3.mean(data, d => d.value);
      const medianValue = d3.median(data, d => d.value);
      const stdDeviation = d3.deviation(data, d => d.value);
      const varianceValue = d3.variance(data, d => d.value);

      const keyData = [];

      // Mean line and legend
      if (this.mean) {
        if (meanValue === undefined) {
          keyData.push({ label: 'Mean', color: 'red', value: 0 });
        } else {
          keyData.push({ label: 'Mean', color: 'red', value: meanValue.toFixed(2) });
        }
        svg
          .append('line')
          .attr('class', 'mean-line')
          .attr('x1', 0)
          .attr('x2', width)
          .attr('y1', yScale(meanValue))
          .attr('y2', yScale(meanValue))
          .attr('stroke', 'red')
          .attr('stroke-width', 2);
      }

      // Median line and legend
      if (this.median) {
        if (medianValue === undefined) {
          keyData.push({ label: 'Median', color: 'blue', value: 0 });
        } else {
          keyData.push({ label: 'Median', color: 'blue', value: medianValue.toFixed(2) });
        }
        svg
          .append('line')
          .attr('class', 'median-line')
          .attr('x1', 0)
          .attr('x2', width)
          .attr('y1', yScale(medianValue))
          .attr('y2', yScale(medianValue))
          .attr('stroke', 'blue')
          .attr('stroke-width', 2);
      }

      // Standard deviation lines and legend
      if (this.standDeviation) {
        if (stdDeviation === undefined) {
          keyData.push({ label: 'SD', color: 'green', value: 0 });
        } else {
          keyData.push({ label: 'SD', color: 'green', value: stdDeviation.toFixed(2) });
        }
        svg
          .append('line')
          .attr('class', 'std-deviation-line')
          .attr('x1', 0)
          .attr('x2', width)
          .attr('y1', yScale(meanValue + stdDeviation))
          .attr('y2', yScale(meanValue + stdDeviation))
          .attr('stroke', 'green')
          .attr('stroke-width', 2);

        svg
          .append('line')
          .attr('class', 'std-deviation-line')
          .attr('x1', 0)
          .attr('x2', width)
          .attr('y1', yScale(meanValue - stdDeviation))
          .attr('y2', yScale(meanValue - stdDeviation))
          .attr('stroke', 'green')
          .attr('stroke-width', 2);
      }

      // Variance line and legend
      if (this.variance) {
        if (varianceValue === undefined) {
          keyData.push({ label: 'Variance', color: 'orange', value: 0 });
        } else {
          keyData.push({ label: 'Variance', color: 'orange', value: varianceValue.toFixed(2) });
        }
        svg
          .append('line')
          .attr('class', 'variance-line')
          .attr('x1', 0)
          .attr('x2', width)
          .attr('y1', yScale(varianceValue))
          .attr('y2', yScale(varianceValue))
          .attr('stroke', 'orange')
          .attr('stroke-width', 2);
      }

      // Create legend/key group elements
      const keySvg = d3.select('#barChartVert');
      const keyItems = keySvg.selectAll('.key-item')
        .data(keyData)
        .enter()
        .append('g')
        .attr('class', 'key-item')
        .attr('transform', (d, i) => `translate(${width + margin.left + 40}, ${i * 24 + 35})`); // 24px vertical spacing

      // Colored lines in legend
      keyItems
        .append('line')
        .attr('x1', 0)
        .attr('y1', 5)
        .attr('x2', 20)
        .attr('y2', 5)
        .attr('stroke', d => d.color)
        .attr('stroke-width', 2);

      // Text labels in legend
      keyItems
        .append('text')
        .attr('x', 30)
        .attr('y', 10)
        .attr('dy', '0.35em')
        .attr('font-size', '10px')
        .text(d => `${d.label}:`)
        .style('fill', 'black');

      // Numeric values in legend
      keyItems
        .append('text')
        .attr('x', 100)
        .attr('y', 10)
        .attr('dy', '0.35em')
        .attr('font-size', '10px')
        .text(d => `${d.value}`)
        .style('fill', 'black');

    }
  },
};
</script>